import { useEffect, useRef } from 'react'
import { useAtomValue } from 'jotai'
import { accessTokenAtom } from '../store/userAtoms'
import { refreshAccessToken } from '../modules/apiClients'
import { isAccessTokenOlderThan } from '../utils/jwt'

// Renew the in-memory access token at most once per this window, on user activity or
// tab re-focus. The token lives 1h, so refreshing after 30min of activity keeps an
// active session's token fresh — requests never carry an expired token, which is what
// caused the "Invalid or expired access token" bursts in Sentry. Crucially there is NO
// background timer: an idle or blurred tab is left to lapse, so the session still dies
// once the refresh cookie expires (no immortal unattended sessions). 🏴‍☠️⭐
const MIN_REFRESH_INTERVAL_MS = 30 * 60_000

/**
 * Keeps an actively-used session warm by refreshing the access token on activity/focus,
 * while letting idle/blurred tabs expire naturally. Mount once (in AuthInitializer).
 * Reuses the shared, deduped refreshAccessToken, which logs the user out itself if the
 * refresh cookie has expired (e.g. a tab left open for more than a day).
 */
export const useSessionKeepAlive = () => {
  const accessToken = useAtomValue(accessTokenAtom)
  const accessTokenRef = useRef(accessToken)

  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  useEffect(() => {
    const refreshIfStale = () => {
      const token = accessTokenRef.current
      // Skip when signed out, or when the token is still fresh (throttle): refreshing
      // every interaction would needlessly rotate tokens. The age gate also means a
      // just-refreshed token won't trigger another refresh for 30min.
      if (!token || !isAccessTokenOlderThan(token, MIN_REFRESH_INTERVAL_MS)) return
      void refreshAccessToken().catch(() => {
        /* failed refresh => logout already handled inside refreshAccessToken */
      })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfStale()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', refreshIfStale)
    window.addEventListener('pointerdown', refreshIfStale, { passive: true })
    window.addEventListener('keydown', refreshIfStale)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', refreshIfStale)
      window.removeEventListener('pointerdown', refreshIfStale)
      window.removeEventListener('keydown', refreshIfStale)
    }
  }, [])
}
